
import React, { useState } from "react";
import { useApp } from "../context/AppContext";
import { uploadAPI, usersAPI } from "../services/api";
import { isValidAlias, isValidCbu } from "../utils/mpValidation";

export default function Profile() {
  const { currentUser, logout, deleteProduct, products, updateCurrentUser } = useApp();
  const [form, setForm] = useState({
    mercadopago_alias:   currentUser?.mercadopago_alias   || "",
    mercadopago_cbu:     currentUser?.mercadopago_cbu     || "",
    mercadopago_titular: currentUser?.mercadopago_titular || "",
    dni:                 currentUser?.dni                 || "",
    phone:               currentUser?.phone                || "",
  });
  const [dniPhotoFile, setDniPhotoFile] = useState(null);
  const [loading, setLoading]         = useState(false);
  const [success, setSuccess]         = useState(false);
  const [error, setError]             = useState("");
  const [showAddForm, setShowAddForm] = useState(!currentUser?.mercadopago_alias && !currentUser?.mercadopago_cbu);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const hasMp = currentUser?.mercadopago_alias || currentUser?.mercadopago_cbu;

  const handleSave = async () => {
    setError("");
    setSuccess(false);
    if (!form.mercadopago_titular.trim()) { setError("El nombre del titular es obligatorio"); return; }
    if (!form.mercadopago_alias.trim() && !form.mercadopago_cbu.trim()) { setError("Ingresa al menos un alias o CBU"); return; }
    if (form.mercadopago_alias.trim() && !isValidAlias(form.mercadopago_alias.trim())) {
      setError("El alias no es valido. Tiene que tener entre 6 y 20 caracteres (letras, numeros, puntos o guiones, sin espacios).");
      return;
    }
    if (form.mercadopago_cbu.trim() && !isValidCbu(form.mercadopago_cbu.trim())) {
      setError("El CBU/CVU no es valido. Tiene que tener 22 numeros — revisa que lo copiaste bien, puede que tenga un digito mal tipeado.");
      return;
    }
    if (!form.dni.trim())   { setError("El DNI es obligatorio"); return; }
    if (!form.phone.trim()) { setError("El celular es obligatorio"); return; }
    if (!dniPhotoFile && !currentUser?.dni_photo_id) { setError("Subi una foto de tu DNI"); return; }

    setLoading(true);
    try {
      const changes = {
        mercadopago_alias:   form.mercadopago_alias.trim(),
        mercadopago_cbu:     form.mercadopago_cbu.trim(),
        mercadopago_titular: form.mercadopago_titular.trim(),
        dni:                 form.dni.trim(),
        phone:               form.phone.trim(),
      };
      await usersAPI.updateMe(changes);

      if (dniPhotoFile) {
        const { dniPhotoId } = await uploadAPI.uploadDniPhoto(dniPhotoFile, currentUser.id);
        changes.dni_photo_id = dniPhotoId;
      }

      updateCurrentUser(changes);
      setDniPhotoFile(null);
      setSuccess(true);
      setShowAddForm(false);
    } catch(e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteMp = async () => {
    setDeleteLoading(true);
    try {
      // Borrar datos de MP
      await usersAPI.updateMe({
        mercadopago_alias:   null,
        mercadopago_cbu:     null,
        mercadopago_titular: null,
      });

      // Borrar todas sus publicaciones
      const myProducts = products.filter(p => p.sellerId === currentUser.id);
      for (const p of myProducts) {
        await deleteProduct(p.id);
      }

      updateCurrentUser({ mercadopago_alias: null, mercadopago_cbu: null, mercadopago_titular: null });

      setConfirmDelete(false);
      setShowAddForm(true);
      setForm({ mercadopago_alias:"", mercadopago_cbu:"", mercadopago_titular:"" });
      alert("✅ Cuenta de Mercado Pago eliminada. Tus publicaciones fueron borradas.");
    } catch(e) {
      alert("Error al eliminar: " + e.message);
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div style={s.page}>
      <h1 style={s.heading}>Mi perfil</h1>

      {/* Info personal */}
      <div style={s.card}>
        <div style={s.avatarWrap}>
          <div style={s.avatar}>{currentUser?.name?.charAt(0).toUpperCase()}</div>
          <div>
            <p style={s.name}>{currentUser?.name}</p>
            <p style={s.email}>{currentUser?.email}</p>
            {currentUser?.community && <p style={s.meta}>🕍 {currentUser.community}</p>}
            {currentUser?.rabbi      && <p style={s.meta}>✡️ {currentUser.rabbi}</p>}
          </div>
        </div>
      </div>

      {/* Datos de MP */}
      <div style={s.card}>
        <h2 style={s.cardTitle}>💳 Cuenta de Mercado Pago</h2>
        <p style={s.cardDesc}>
          Necesitás tener tu cuenta de Mercado Pago cargada para poder publicar y recibir pagos.
        </p>

        {/* Si ya tiene MP cargado y no está editando */}
        {hasMp && !showAddForm ? (
          <div>
            <div style={s.mpDataBox}>
              <div style={s.mpDataRow}>
                <span style={s.mpDataLabel}>👤 Titular</span>
                <span style={s.mpDataValue}>{currentUser.mercadopago_titular || "—"}</span>
              </div>
              {currentUser.mercadopago_alias && (
                <div style={s.mpDataRow}>
                  <span style={s.mpDataLabel}>🏷️ Alias</span>
                  <span style={s.mpDataValue}>{currentUser.mercadopago_alias}</span>
                </div>
              )}
              {currentUser.mercadopago_cbu && (
                <div style={s.mpDataRow}>
                  <span style={s.mpDataLabel}>🔢 CBU/CVU</span>
                  <span style={s.mpDataValue}>{currentUser.mercadopago_cbu}</span>
                </div>
              )}
              <div style={s.mpDataRow}>
                <span style={s.mpDataLabel}>🪪 DNI</span>
                <span style={s.mpDataValue}>{currentUser.dni || "—"}</span>
              </div>
              <div style={s.mpDataRow}>
                <span style={s.mpDataLabel}>📱 Celular</span>
                <span style={s.mpDataValue}>{currentUser.phone || "—"}</span>
              </div>
              <div style={s.mpDataRow}>
                <span style={s.mpDataLabel}>🪪 Foto DNI</span>
                <span style={s.mpDataValue}>{currentUser.dni_photo_id ? "✅ Cargada" : "—"}</span>
              </div>
            </div>

            <div style={s.mpBtns}>
              <button style={s.btnEdit} onClick={() => { setShowAddForm(true); setSuccess(false); }}>
                ✏️ Editar cuenta
              </button>
              <button style={s.btnDelete} onClick={() => setConfirmDelete(true)}>
                🗑️ Eliminar cuenta
              </button>
            </div>

            {/* Confirmacion de borrado */}
            {confirmDelete && (
              <div style={s.confirmBox}>
                <p style={s.confirmTitle}>⚠️ Confirmar eliminacion</p>
                <p style={s.confirmText}>
                  Si eliminás tu cuenta de Mercado Pago, <strong>todas tus publicaciones activas se van a borrar automaticamente</strong>.
                  Esta accion no se puede deshacer.
                </p>
                <div style={s.confirmBtns}>
                  <button style={s.btnCancelConfirm} onClick={() => setConfirmDelete(false)}>Cancelar</button>
                  <button
                    style={{...s.btnConfirmDelete, opacity: deleteLoading?0.7:1}}
                    onClick={handleDeleteMp}
                    disabled={deleteLoading}
                  >
                    {deleteLoading ? "Eliminando..." : "Si, eliminar todo"}
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          /* Formulario para agregar/editar */
          <div>
            {hasMp && (
              <button style={s.btnCancelEdit} onClick={() => setShowAddForm(false)}>
                ← Volver
              </button>
            )}

            <div style={s.field}>
              <label style={s.label}>Nombre del titular *</label>
              <input
                style={s.input}
                placeholder="Ej: Maria Garcia"
                value={form.mercadopago_titular}
                onChange={e => setForm(f => ({...f, mercadopago_titular: e.target.value}))}
              />
              <p style={s.hint}>Nombre y apellido tal como aparece en tu cuenta de MP</p>
            </div>

            <div style={s.field}>
              <label style={s.label}>Alias de Mercado Pago</label>
              <input
                style={s.input}
                placeholder="Ej: mi.alias.mp"
                value={form.mercadopago_alias}
                onChange={e => setForm(f => ({...f, mercadopago_alias: e.target.value}))}
              />
            </div>

            <div style={s.field}>
              <label style={s.label}>CBU / CVU (opcional)</label>
              <input
                style={s.input}
                placeholder="Ej: 0000003100012345678901"
                value={form.mercadopago_cbu}
                onChange={e => setForm(f => ({...f, mercadopago_cbu: e.target.value.replace(/\D/g, "")}))}
                maxLength={22}
              />
              <p style={s.hint}>Podés poner alias o CBU, o ambos</p>
            </div>

            <div style={s.field}>
              <label style={s.label}>DNI *</label>
              <input
                style={s.input}
                type="tel"
                inputMode="numeric"
                placeholder="Ej: 30123456"
                value={form.dni}
                onChange={e => setForm(f => ({...f, dni: e.target.value.replace(/\D/g, "")}))}
              />
            </div>

            <div style={s.field}>
              <label style={s.label}>Celular *</label>
              <input
                style={s.input}
                type="tel"
                inputMode="numeric"
                placeholder="Ej: 1123456789"
                value={form.phone}
                onChange={e => setForm(f => ({...f, phone: e.target.value.replace(/\D/g, "")}))}
              />
            </div>

            <div style={s.field}>
              <label style={s.label}>Foto de tu DNI (frente) *</label>
              <input
                style={s.input}
                type="file"
                accept="image/*"
                onChange={e => setDniPhotoFile(e.target.files?.[0] || null)}
              />
              {currentUser?.dni_photo_id && !dniPhotoFile && (
                <p style={s.hint}>Ya tenés una foto cargada. Subí una nueva solo si queres reemplazarla.</p>
              )}
              {dniPhotoFile && <p style={s.hint}>Seleccionaste: {dniPhotoFile.name}</p>}
              <p style={s.confidentialNote}>
                🔒 Esta foto es <strong>privada y confidencial</strong>: solo la pueden ver los administradores,
                y unicamente para verificar tu identidad si hay algun problema con una venta. Si detectan algo
                irregular, tu cuenta o publicaciones pueden ser canceladas.
              </p>
            </div>

            {error   && <p style={s.error}>❌ {error}</p>}
            {success && <p style={s.successMsg}>✅ Datos guardados correctamente!</p>}

            <button
              style={{...s.btnSave, opacity: loading?0.7:1}}
              onClick={handleSave}
              disabled={loading}
            >
              {loading ? "Guardando..." : hasMp ? "Actualizar datos" : "Guardar cuenta de MP"}
            </button>
          </div>
        )}
      </div>

      {/* Info */}
      <div style={s.infoBox}>
        <p style={s.infoTitle}>ℹ️ Como funciona el pago</p>
        <p style={s.infoText}>1. El comprador confirma la entrega con el codigo unico</p>
        <p style={s.infoText}>2. Los administradores reciben una notificacion con tu alias</p>
        <p style={s.infoText}>3. El pago se transfiere a tu alias/CBU dentro de las 24-48hs</p>
        <p style={s.infoText}>4. Vos recibis el 95% del precio (5% es la comision de la plataforma) — tus primeras 2 ventas no tienen comision</p>
      </div>
    </div>
  );
}

const s = {
  page:{maxWidth:600,margin:"0 auto",padding:"24px 16px",display:"flex",flexDirection:"column",gap:16},
  heading:{fontSize:22,fontWeight:600},
  card:{background:"#fff",border:"0.5px solid #e0e0e0",borderRadius:14,padding:"20px 24px"},
  avatarWrap:{display:"flex",alignItems:"center",gap:16},
  avatar:{width:56,height:56,borderRadius:"50%",background:"#E1F5EE",color:"#0F6E56",display:"flex",alignItems:"center",justifyContent:"center",fontSize:24,fontWeight:700,flexShrink:0},
  name:{fontSize:18,fontWeight:600,color:"#111",marginBottom:4},
  email:{fontSize:14,color:"#888",marginBottom:4},
  meta:{fontSize:13,color:"#666"},
  cardTitle:{fontSize:16,fontWeight:600,color:"#111",marginBottom:8},
  cardDesc:{fontSize:13,color:"#666",lineHeight:1.6,marginBottom:20},
  mpDataBox:{background:"#f0faf6",borderRadius:10,padding:"16px",marginBottom:16},
  mpDataRow:{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 0",borderBottom:"0.5px solid #e0f0e8"},
  mpDataLabel:{fontSize:13,color:"#888"},
  mpDataValue:{fontSize:14,fontWeight:500,color:"#111"},
  mpBtns:{display:"flex",gap:10},
  btnEdit:{flex:1,padding:"10px 0",background:"#f0faf6",color:"#0F6E56",border:"0.5px solid #b0dcc9",borderRadius:10,fontSize:14,fontWeight:500,cursor:"pointer"},
  btnDelete:{flex:1,padding:"10px 0",background:"#FEE",color:"#c0392b",border:"0.5px solid #f0b0b0",borderRadius:10,fontSize:14,cursor:"pointer"},
  confirmBox:{background:"#fff8e1",border:"1.5px solid #f0c040",borderRadius:12,padding:"16px",marginTop:16},
  confirmTitle:{fontSize:15,fontWeight:600,color:"#b7830a",marginBottom:8},
  confirmText:{fontSize:13,color:"#555",lineHeight:1.6,marginBottom:14},
  confirmBtns:{display:"flex",gap:10},
  btnCancelConfirm:{flex:1,padding:"10px 0",background:"#fff",border:"0.5px solid #ddd",borderRadius:10,fontSize:14,cursor:"pointer"},
  btnConfirmDelete:{flex:1,padding:"10px 0",background:"#E24B4A",color:"#fff",border:"none",borderRadius:10,fontSize:14,fontWeight:600,cursor:"pointer"},
  btnCancelEdit:{background:"none",border:"none",color:"#1D9E75",fontSize:14,cursor:"pointer",marginBottom:16,padding:0},
  field:{marginBottom:16},
  label:{display:"block",fontSize:13,fontWeight:500,color:"#555",marginBottom:6},
  input:{width:"100%",padding:"11px 14px",borderRadius:10,border:"0.5px solid #ddd",fontSize:15},
  hint:{fontSize:12,color:"#aaa",marginTop:4},
  confidentialNote:{fontSize:12,color:"#8a6410",background:"#fff8e1",border:"0.5px solid #f5dfa0",borderRadius:8,padding:"10px 12px",marginTop:8,lineHeight:1.5},
  error:{color:"#E24B4A",fontSize:13,marginBottom:12},
  successMsg:{color:"#0F6E56",fontSize:13,marginBottom:12,fontWeight:500},
  btnSave:{width:"100%",background:"#1D9E75",color:"#fff",border:"none",borderRadius:10,padding:"13px",fontSize:15,fontWeight:600,cursor:"pointer"},
  infoBox:{background:"#f0faf6",border:"0.5px solid #b0dcc9",borderRadius:12,padding:"16px 20px"},
  infoTitle:{fontSize:14,fontWeight:600,color:"#0F6E56",marginBottom:10},
  infoText:{fontSize:13,color:"#555",marginBottom:6,lineHeight:1.5},
};
